import React, { useEffect, useState } from 'react';
import './ChampionSummonerSpells.css';
import DataService from '../../../XXX/DataService';

interface SummonerSpell {
  spell1: number;
  spell2: number;
  win_rate: number;
  pick_rate: number;
  count: number;
  spell1_name?: string;
  spell2_name?: string;
}

interface Props {
  championName: string;
}

const ChampionSummonerSpells: React.FC<Props> = ({ championName }) => {
  const [loading, setLoading] = useState(true);
  const [spellsData, setSpellsData] = useState<SummonerSpell[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [apiVersion, setApiVersion] = useState<string>('15.23.1');
  
  // Fetch latest API version
  useEffect(() => {
    DataService.fetchLatestVersion().then(setApiVersion);
  }, []);
  
  useEffect(() => {
    const fetchSummonerSpells = async () => {
      try {
        setLoading(true);
        console.log(`Fetching summoner spells for ${championName}...`);
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/stats/champion/${championName}/summoner-spells`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch summoner spells: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Fetched summoner spells data:', data);
        
        if (!data || data.length === 0) {
          console.warn(`No summoner spell data returned for ${championName}`);
          setError(`No summoner spell data available for ${championName}`);
        } else {
          setSpellsData(data);
        }
      } catch (err) {
        console.error('Error fetching summoner spells:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setLoading(false);
      }
    };
    
    if (championName) {
      fetchSummonerSpells();
    }
  }, [championName]);
  
  if (loading) {
    return null; // Don't show anything while loading
  }
  
  if (error || !spellsData || spellsData.length === 0) {
    return null; // Don't show anything on error
  }

  // Get only the most popular summoner spell combination
  const popularSpellCombo = spellsData[0];
  
  // Helper to get spell image URL
  const getSpellImageUrl = (spellId: number) => {
    return `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/spell/Summoner${getSpellKeyById(spellId)}.png`;
  };
  
  // Map of spell IDs to their Data Dragon image keys
  const getSpellKeyById = (spellId: number): string => {
    // Common spell mappings (values must match the ddragon filename after "Summoner")
    const spellMapping: Record<number, string> = {
      1: 'Boost',       // Cleanse
      3: 'Exhaust',
      4: 'Flash',
      6: 'Haste',       // Ghost
      7: 'Heal',
      11: 'Smite',
      12: 'Teleport',
      14: 'Dot',        // Ignite
      21: 'Barrier',
      32: 'Snowball'    // Mark / Snow Ball
    };
    
    return spellMapping[spellId] || 'Flash'; // Default to Flash if unknown
  };
  
  return (
    <div className="ss-simple-container">
      <div className="ss-simple-spells">
        <img 
          src={getSpellImageUrl(popularSpellCombo.spell1)} 
          alt={popularSpellCombo.spell1_name || getSpellKeyById(popularSpellCombo.spell1)} 
          className="ss-simple-img" 
        />
        <img 
          src={getSpellImageUrl(popularSpellCombo.spell2)} 
          alt={popularSpellCombo.spell2_name || getSpellKeyById(popularSpellCombo.spell2)} 
          className="ss-simple-img" 
        />
      </div>
    </div>
  );
};

export default ChampionSummonerSpells; 